import PlayArrowRounded from "@mui/icons-material/PlayArrowRounded";
import { Box, Button, Chip, Paper, TextField, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import React from "react";
import { DateRangeField } from "./DateRangeField";
import { EVENT_LABELS, EVENT_TYPES } from "./eventTypes";
import { MemberPicker } from "./MemberPicker";
import { RegionFields, ReportFilters, ReportFilterState } from "./ReportFilters";
import { TotalsParams } from "./Reports";
import { Granularity } from "./reportFormat";
import { OtpChannel, ReportDefinition, ReportKey, ReportParams } from "./reportParams";
import { COMPARE_PRESETS, ComparePreset } from "./scorecardFormat";

const DAILY_MAX_DAYS = 31;

const Fields: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, alignItems: "center" }}>{children}</Box>
);

/** The controls of one report. Each edit only changes the pane: nothing is asked for until Run. */
const ReportFields: React.FC<{
  report: ReportKey;
  params: ReportParams;
  onChange: (next: ReportParams) => void;
}> = ({ report, params, onChange }) => {
  const filters = params.filters as ReportFilterState;
  const setFilters = (next: ReportFilterState) => onChange({ ...params, filters: next });

  switch (report) {
    case "scorecard": {
      const { preset, custom } = params.scorecard;
      const setCompare = (next: Partial<ReportParams["scorecard"]>) =>
        onChange({ ...params, scorecard: { ...params.scorecard, ...next } });
      return (
        <Fields>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={preset}
            onChange={(_, value: ComparePreset | null) => value && setCompare({ preset: value })}
            aria-label="Compare"
          >
            {COMPARE_PRESETS.map((item) => (
              <ToggleButton key={item.key} value={item.key}>
                {item.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
          {preset === "custom" && (
            <>
              <DateRangeField
                label="This period"
                presets={[]}
                maxDays={366}
                value={custom.current}
                onChange={(current) => setCompare({ custom: { ...custom, current } })}
              />
              <DateRangeField
                label="Compared with"
                presets={[]}
                maxDays={366}
                value={custom.previous}
                onChange={(previous) => setCompare({ custom: { ...custom, previous } })}
              />
            </>
          )}
          <RegionFields value={filters} onChange={setFilters} />
        </Fields>
      );
    }
    case "daily":
      return (
        <Fields>
          <DateRangeField
            value={params.daily}
            onChange={(daily) => onChange({ ...params, daily })}
            maxDays={DAILY_MAX_DAYS}
            presets={["7d", "30d"]}
          />
          <Typography variant="body2" color="text.secondary">
            Up to {DAILY_MAX_DAYS} days, a column each.
          </Typography>
        </Fields>
      );
    case "weekly":
    case "monthly":
      return (
        <Fields>
          <DateRangeField
            value={params[report]}
            onChange={(dates) => onChange({ ...params, [report]: dates })}
            maxDays={DAILY_MAX_DAYS}
            presets={[]}
          />
          <Typography variant="body2" color="text.secondary">
            Opens on the last complete {report === "weekly" ? "week" : "month"}. A column per day, and the total at the end.
          </Typography>
        </Fields>
      );
    case "engagement":
      return <ReportFilters value={filters} onChange={setFilters} />;
    case "retention":
      return (
        <Fields>
          <ReportFilters value={filters} onChange={setFilters} showGranularity={false} />
          <ToggleButtonGroup
            size="small"
            exclusive
            value={params.retentionCohort}
            onChange={(_, value: Granularity | null) => value && onChange({ ...params, retentionCohort: value })}
            aria-label="Group members by"
          >
            <ToggleButton value="WEEK">By week</ToggleButton>
            <ToggleButton value="MONTH">By month</ToggleButton>
          </ToggleButtonGroup>
        </Fields>
      );
    case "events": {
      const { types, member } = params.events;
      const setEvents = (next: Partial<ReportParams["events"]>) =>
        onChange({ ...params, events: { ...params.events, ...next } });
      return (
        <Box sx={{ display: "grid", gap: 1.5 }}>
          <Fields>
            <ReportFilters value={filters} onChange={setFilters} showGranularity={false} />
            <MemberPicker value={member} onChange={(next) => setEvents({ member: next })} />
          </Fields>
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.75 }}>
              Which kinds. None picked lists them all.
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
              {EVENT_TYPES.map((type) => {
                const on = types.includes(type);
                return (
                  <Chip
                    key={type}
                    label={EVENT_LABELS[type].plural}
                    color={on ? "primary" : "default"}
                    variant={on ? "filled" : "outlined"}
                    aria-pressed={on}
                    onClick={() => setEvents({ types: on ? types.filter((item) => item !== type) : [...types, type] })}
                  />
                );
              })}
            </Box>
          </Box>
        </Box>
      );
    }
    case "screens":
      return (
        <Fields>
          <ReportFilters value={filters} onChange={setFilters} showGranularity={false} />
          <MemberPicker
            value={params.screens.member}
            onChange={(member) => onChange({ ...params, screens: { member } })}
          />
        </Fields>
      );
    case "otp": {
      const setOtp = (next: Partial<ReportParams["otp"]>) => onChange({ ...params, otp: { ...params.otp, ...next } });
      return (
        <Fields>
          <ReportFilters value={filters} onChange={setFilters} showRegion={false} />
          <ToggleButtonGroup
            size="small"
            exclusive
            value={params.otp.channel}
            onChange={(_, channel: OtpChannel | null) => channel && setOtp({ channel })}
            aria-label="Channel"
          >
            <ToggleButton value="all">Text and email</ToggleButton>
            <ToggleButton value="sms">Text</ToggleButton>
            <ToggleButton value="email">Email</ToggleButton>
          </ToggleButtonGroup>
          <TextField
            size="small"
            label="Number or email"
            value={params.otp.destination}
            onChange={(event) => setOtp({ destination: event.target.value })}
            sx={{ minWidth: 260 }}
          />
        </Fields>
      );
    }
    case "totals":
      return <TotalsParams value={params.totals} onChange={(totals) => onChange({ ...params, totals })} />;
  }
};

/**
 * The parameter pane: the open report's controls and Run. A form, so Enter in
 * any field runs it too. Keyed by report by the page, so a half-typed search in
 * one report's picker does not follow to the next.
 */
export const ReportParamsPane: React.FC<{
  report: ReportDefinition;
  params: ReportParams;
  onChange: (next: ReportParams) => void;
  onRun: () => void;
}> = ({ report, params, onChange, onRun }) => (
  <Paper
    component="form"
    aria-label={`${report.label} parameters`}
    variant="outlined"
    noValidate
    onSubmit={(event: React.FormEvent) => {
      event.preventDefault();
      onRun();
    }}
    sx={{ p: 2, display: "grid", gap: 1.5 }}
  >
    <Box>
      <Typography variant="h5" component="h2">
        {report.label}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {report.description}
      </Typography>
    </Box>
    <ReportFields report={report.key} params={params} onChange={onChange} />
    <Box>
      <Button type="submit" variant="contained" startIcon={<PlayArrowRounded />} sx={{ width: { xs: "100%", sm: "auto" } }}>
        Run
      </Button>
    </Box>
  </Paper>
);
